/**
 * `<pixel-avatar seed="12345" size="24"><canvas></canvas></pixel-avatar>`
 *
 * Deterministic pixel-art face avatar (Face Hash v2). Shared generator behind
 * the account menu trigger and the `avatar` option in the icon-or-image
 * snippet, so both stay visually identical and in sync.
 *
 * 12×12 logical grid, buffer rendered at 3x the CSS `size` for crisp
 * nearest-neighbour upscaling (matches the theme's 2px pixel weight at the
 * default 24px size, i.e. a 72×72 buffer displayed at 24px CSS):
 *   - Row/col 0 & 11: full-cell border in currentColor, corners excluded
 *   - Row/col 1 & 10: transparent gap
 *   - Rows/cols 2–9: 8×8 inner avatar — dark bg + 4-way symmetric brand pixels
 *
 * Reads `--pixel-avatar-brand` / `--pixel-avatar-bg` custom properties from
 * the host so the avatar stays in sync with the theme's colour settings.
 */
export class PixelAvatar extends HTMLElement {
  static get observedAttributes() {
    return ['seed', 'size'];
  }

  #observer = null;

  connectedCallback() {
    this.paint();
    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.paint();
          }
        }
      });
      this.#observer.observe(this);
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  attributeChangedCallback() {
    if (this.isConnected) this.paint();
  }

  /**
   * Paint (or repaint) the avatar. Exposed publicly so parent components can
   * force a repaint after a Section Rendering API morph, in case the morph
   * touched the canvas's width/height attributes and cleared its buffer.
   */
  paint() {
    const canvas = this.querySelector('canvas');
    if (!(canvas instanceof HTMLCanvasElement)) return;

    const seed = parseInt(this.getAttribute('seed') ?? '0', 10);
    const size = parseInt(this.getAttribute('size') ?? '24', 10);
    const h = faceHashV2(seed);

    const px = (size * 3) / 12;

    canvas.width = size * 3;
    canvas.height = size * 3;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const cs = getComputedStyle(canvas);
    // Read from CSS custom properties so the avatar stays in sync with theme settings.
    // Fall back to hardcoded values if the properties aren't set.
    const brand = cs.getPropertyValue('--pixel-avatar-brand').trim() || '#ff6363';
    const dark = cs.getPropertyValue('--pixel-avatar-bg').trim() || '#1a0a0a';
    // Read CSS currentColor so the border adapts to the icon colour in any context.
    const border = cs.color;

    // Dark background for the 8×8 inner face area
    ctx.fillStyle = dark;
    for (let r = 2; r <= 9; r++) {
      for (let c = 2; c <= 9; c++) {
        ctx.fillRect(c * px, r * px, px, px);
      }
    }

    // 4-way symmetric face pixels — 16 bits from faceHashV2, anatomy pre-baked in.
    ctx.fillStyle = brand;
    let bit = 0;
    for (let row = 0; row < 4; row++) {
      for (let col = 0; col < 4; col++) {
        if ((h >>> bit++) & 1) {
          for (const r of [row, 7 - row]) {
            for (const c of [col, 7 - col]) {
              ctx.fillRect((c + 2) * px, (r + 2) * px, px, px);
            }
          }
        }
      }
    }

    // Pixel-art border: one full cell thick, corners excluded.
    ctx.fillStyle = border;
    for (let i = 1; i <= 10; i++) {
      ctx.fillRect(i * px, 0, px, px); // top
      ctx.fillRect(i * px, 11 * px, px, px); // bottom
      ctx.fillRect(0, i * px, px, px); // left
      ctx.fillRect(11 * px, i * px, px, px); // right
    }
  }
}

/**
 * Face Hash v2 — 16-bit face pattern from a 5-digit customer ID seed.
 *
 * 8 bits are forced to anatomically correct values (brow ridge, face walls,
 * eye hollows, clear nose bridge, eye dots, no jaw blobs, clear centre).
 * 8 personality bits are sampled from well-separated positions in the hash.
 * → 256 unique faces, all structurally sound.
 *
 * @param {number} seed  5-digit numeric seed (last 5 digits of customer.id)
 * @returns {number}     16-bit face pattern as unsigned 32-bit integer
 */
function faceHashV2(seed) {
  const h = hash32(seed);

  const b0 = (h >>> 1) & 1; // head corner
  const b1 = (h >>> 2) & 1; // head top outer
  const b2 = (h >>> 3) & 1; // head top inner
  const b3 = (h >>> 4) & 1; // head top centre
  const b4 = (h >>> 8) & 1; // brow outer
  const b6 = (h >>> 9) & 1; // brow inner variation
  const b7 = (h >>> 16) & 1; // brow centre
  const b12 = (h >>> 24) & 1; // chin / jaw walls

  return (
    (b0 << 0) |
    (b1 << 1) |
    (b2 << 2) |
    (b3 << 3) |
    (b4 << 4) |
    (1 << 5) | // b5  forced ON  — inner brow ridge
    (b6 << 6) |
    (b7 << 7) |
    (1 << 8) | // b8  forced ON  — face walls
    // b9  forced OFF — eye outer hollow
    // b10 forced OFF — eye inner hollow
    // b11 forced OFF — nose bridge clear
    (b12 << 12) |
    // b13 forced OFF — no jaw blob
    (1 << 14) // b14 forced ON  — eye dots
    // b15 forced OFF — centre clear
  ) >>> 0;
}

/**
 * Murmur3-finaliser style 32-bit hash — fast, good bit dispersion.
 * @param {number} n
 * @returns {number} unsigned 32-bit integer
 */
function hash32(n) {
  n = Math.imul(n ^ (n >>> 16), 0x45d9f3b);
  n = Math.imul(n ^ (n >>> 16), 0x45d9f3b);
  return (n ^ (n >>> 16)) >>> 0;
}

if (!customElements.get('pixel-avatar')) {
  customElements.define('pixel-avatar', PixelAvatar);
}
